import type { SxProps, Theme } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';

export const HOME_GRID_MAX_WIDTH = 1500;

/** Columns: mobile 3 · tablet 4 · desktop 5 */
export const HOME_GRID_COLUMNS = {
    mobile: 3,
    tablet: 4,
    desktop: 5
} as const;

export type HomeGridLayout = {
    columnCount: number;
    columnGap: string;
    rowGap: string;
    containerPadding: string;
    cardHeight: number;
    /** card height + row gap — used by the virtualizer stride */
    rowStride: number;
};

function spacingPx(theme: Theme, factor: number): number {
    return parseFloat(theme.spacing(factor));
}

export function getHomeGridLayout(theme: Theme, isMobile: boolean, isTablet: boolean): HomeGridLayout {
    if (isMobile) {
        const rowGap = theme.spacing(1.5);
        const cardHeight = 248;
        return {
            columnCount: HOME_GRID_COLUMNS.mobile,
            columnGap: rowGap,
            rowGap,
            containerPadding: theme.spacing(2),
            cardHeight,
            rowStride: cardHeight + spacingPx(theme, 1.5)
        };
    }

    if (isTablet) {
        const rowGap = theme.spacing(2);
        const cardHeight = 300;
        return {
            columnCount: HOME_GRID_COLUMNS.tablet,
            columnGap: rowGap,
            rowGap,
            containerPadding: theme.spacing(2),
            cardHeight,
            rowStride: cardHeight + spacingPx(theme, 2)
        };
    }

    const rowGap = theme.spacing(3);
    const cardHeight = 330;
    return {
        columnCount: HOME_GRID_COLUMNS.desktop,
        columnGap: rowGap,
        rowGap,
        containerPadding: theme.spacing(2),
        cardHeight,
        rowStride: cardHeight + spacingPx(theme, 3)
    };
}

export function useHomeGridLayout(): HomeGridLayout {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    return useMemo(() => getHomeGridLayout(theme, isMobile, isTablet), [theme, isMobile, isTablet]);
}

/** Full grid (loading skeleton) — column + row gap from layout tokens */
export function homeGridSx(layout: HomeGridLayout): SxProps<Theme> {
    return {
        display: 'grid',
        width: '100%',
        boxSizing: 'border-box',
        gridTemplateColumns: `repeat(${layout.columnCount}, minmax(0, 1fr))`,
        columnGap: layout.columnGap,
        rowGap: layout.rowGap
    };
}

/** Single virtualized row — column gap matches container; row gap via padding-bottom */
export function homeGridRowSx(layout: HomeGridLayout): SxProps<Theme> {
    return {
        display: 'grid',
        width: '100%',
        boxSizing: 'border-box',
        gridTemplateColumns: `repeat(${layout.columnCount}, minmax(0, 1fr))`,
        columnGap: layout.columnGap,
        paddingBottom: layout.rowGap
    };
}
